import { Github } from "lucide-react";
import { useGlobalProgress } from "@/shared/contexts/use-global-progress";
import { check } from "@tauri-apps/plugin-updater";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui";
import { relaunch } from "@tauri-apps/plugin-process";
import { CONTENT_PADDING_RIGHT } from "@/domains/core/layout/SidebarLayoutWidth";
import { getVersion } from "@tauri-apps/api/app";
import { useDebugSystem } from "@/domains/logging/hooks/useDebugSystem";

export const Footer = () => {
  const { isAnySyncActive } = useGlobalProgress();
  const { toggleOverlay } = useDebugSystem();

  const [checking, setChecking] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<string | null>(null);
  const [appVersion, setAppVersion] = useState<string | null>(null);

  useEffect(() => {
    getVersion()
      .then(setAppVersion)
      .catch(() => setAppVersion(null));
  }, []);

  const checkForUpdates = async () => {
    setChecking(true);
    setUpdateInfo(null);
    try {
      // Prüfe ob wir im Development-Modus sind
      const isDev = import.meta.env.DEV;

      if (isDev) {
        setUpdateInfo("⚠️ Update-Check im Development-Modus nicht verfügbar");
        return;
      }

      const update = await check();

      if (!update?.available) {
        setUpdateInfo("✅ App ist auf dem neuesten Stand.");
        return;
      }

      setUpdateInfo(`🚀 Update ${update.version} wird heruntergeladen...`);

      try {
        await update.downloadAndInstall();
        setUpdateInfo("✅ Update installiert. App wird neu gestartet...");
      } catch (downloadError) {
        const errorMessage =
          downloadError instanceof Error ? downloadError.message : "Unbekannter Download-Fehler";
        setUpdateInfo(`❌ Download-Fehler: ${errorMessage}`);
        return;
      }
      await relaunch();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unbekannter Fehler";

      // Spezifische Fehlerbehandlung
      if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
        setUpdateInfo("❌ Netzwerk-Fehler: Endpoint nicht erreichbar");
      } else if (errorMessage.includes("permission") || errorMessage.includes("denied")) {
        setUpdateInfo("❌ Berechtigungs-Fehler: Keine Update-Berechtigung");
      } else if (errorMessage.includes("signature") || errorMessage.includes("verify")) {
        setUpdateInfo("❌ Signatur-Fehler: Update nicht verifiziert");
      } else {
        setUpdateInfo(`❌ Fehler beim Update-Check: ${errorMessage}`);
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <footer
      className="relative w-full py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700/50 transition-all duration-300 ease-in-out"
      style={{
        paddingLeft: CONTENT_PADDING_RIGHT,
        paddingRight: CONTENT_PADDING_RIGHT,
      }}
    >
      {/* Innenstruktur zentrieren und gleichmäßiges Padding geben */}
      <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between">
        {/* Branding */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            TracQ
          </span>
          <div className="h-4 w-px bg-slate-700" />
          <span className="text-xs text-slate-500">Personal LifeOS</span>
        </div>

        {/* GitHub Link und Debug-Link */}
        <div className="flex items-center space-x-4">
          <div className="group">
            <a
              href="https://github.com/iBraska2/tracq-client"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Repository"
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/30 transition-all duration-200 group-hover:translate-y-[-1px]"
            >
              <Github size={16} />
              <span className="text-sm font-medium">GitHub</span>
            </a>
          </div>

          {/* Debug-Link */}
          {import.meta.env.DEV && (
            <div className="group">
              <button
                onClick={toggleOverlay}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/30 transition-all duration-200 group-hover:translate-y-[-1px]"
              >
                <span className="text-sm font-medium">🐛 Debug System</span>
              </button>
            </div>
          )}
        </div>

        {/* Update-Bereich */}
        <div className="flex flex-col items-center space-y-1">
          <Button
            onClick={checkForUpdates}
            disabled={checking}
            variant="ghost"
            size="sm"
            className="text-xs px-3 py-1.5 bg-slate-800/50 border border-slate-600/50 hover:bg-slate-700/50 hover:border-slate-500/50 text-slate-300 hover:text-white transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <span>{checking ? "Suche..." : "Update prüfen"}</span>
              {appVersion && <span className="text-xs text-slate-500 mt-1">{appVersion}</span>}
            </div>
          </Button>

          {updateInfo && (
            <p className="text-xs text-slate-400 max-w-[160px] text-center leading-tight">
              {updateInfo}
            </p>
          )}
        </div>
      </div>

      {/* Sync-Animation */}
      {isAnySyncActive && (
        <div className="absolute bottom-0 left-0 w-full flex justify-center z-50">
          <div className="flex gap-2 py-1">
            <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-bounce" />
            <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-bounce [animation-delay:0.1s]" />
            <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-bounce [animation-delay:0.2s]" />
          </div>
        </div>
      )}
    </footer>
  );
};
